import { useCallback, useMemo } from 'react'

import { IntegrationCategory, type IntegrationServiceId } from '../../../../common/integrations'
import { NoticeKey } from '../../../../models/Notice'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'
import { useGetNoticesQuery, useUpdateNoticeMutation } from '../../../../store/notices/notices'
import { useIntegrations } from '../../../Integration/useIntegrations'
import { useContactCapability } from '../../../../store/onboarding/useContactCapability'
import { useFinancialPlanningCapability } from '../../../../store/onboarding/useFinancialPlanningCapability'

export interface UseContactsEmptyStateResult {
  isFetching: boolean
  shouldShow: boolean
  isDismissing: boolean
  dismiss: () => void
  allowedServiceIds: IntegrationServiceId[]
  connectedWithoutSync: IntegrationServiceId[]
  displayCategory: IntegrationCategory.CRM | IntegrationCategory.PLANNING | null
}

export interface UseFinancialPlanningOnboardingBannerResult {
  isFetching: boolean
  shouldShow: boolean
  isDismissing: boolean
  dismiss: () => void
}

export const useContactsEmptyState = (): UseContactsEmptyStateResult => {
  const accountId = useAppSelector(selectAccountId)
  const contactCapability = useContactCapability()
  const { isIntegrationCategoryAllowed } = useIntegrations()

  const { data: noticesData, isFetching: isNoticesFetching } = useGetNoticesQuery({ accountId }, { skip: contactCapability.isLoading })
  const [updateNotice, { isLoading: isDismissing }] = useUpdateNoticeMutation()

  const isDismissed = noticesData?.data?.find((n) => n.key === NoticeKey.CONTACTS_EMPTY_STATE)?.isDismissed ?? false

  const displayCategory = useMemo<IntegrationCategory.CRM | IntegrationCategory.PLANNING | null>(() => {
    if (isIntegrationCategoryAllowed(IntegrationCategory.CRM)) {
      return IntegrationCategory.CRM
    }
    if (isIntegrationCategoryAllowed(IntegrationCategory.PLANNING)) {
      return IntegrationCategory.PLANNING
    }
    return null
  }, [isIntegrationCategoryAllowed])

  const shouldShow =
    contactCapability.isAllowed && !contactCapability.isFulfilled && !isDismissed && !isDismissing && !isNoticesFetching && displayCategory !== null

  const dismiss = useCallback(() => {
    updateNotice({ accountId, key: NoticeKey.CONTACTS_EMPTY_STATE, isDismissed: true })
  }, [accountId, updateNotice])

  return {
    isFetching: contactCapability.isLoading || isNoticesFetching,
    shouldShow,
    isDismissing,
    dismiss,
    allowedServiceIds: contactCapability.allowedServiceIds,
    connectedWithoutSync: contactCapability.connectedWithoutSync,
    displayCategory,
  }
}

export const useFinancialPlanningOnboardingBanner = (): UseFinancialPlanningOnboardingBannerResult => {
  const accountId = useAppSelector(selectAccountId)
  const financialPlanningCapability = useFinancialPlanningCapability()

  const { data: noticesData, isFetching: isNoticesFetching } = useGetNoticesQuery({ accountId }, { skip: financialPlanningCapability.isLoading })
  const [updateNotice, { isLoading: isDismissing }] = useUpdateNoticeMutation()

  const isDismissed = noticesData?.data?.find((n) => n.key === NoticeKey.CONTACTS_FINANCIAL_PLANNING_ONBOARDING_BANNER)?.isDismissed ?? false

  const shouldShow = financialPlanningCapability.isAllowed && !financialPlanningCapability.isFulfilled && !isDismissed && !isDismissing && !isNoticesFetching

  const dismiss = useCallback(() => {
    updateNotice({ accountId, key: NoticeKey.CONTACTS_FINANCIAL_PLANNING_ONBOARDING_BANNER, isDismissed: true })
  }, [accountId, updateNotice])

  return {
    isFetching: financialPlanningCapability.isLoading || isNoticesFetching,
    shouldShow,
    isDismissing,
    dismiss,
  }
}
